/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik, useFormik } from "formik";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router";
import moment from "helpers/moment";
import Skeleton from "react-loading-skeleton";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService
} from "../../../../resources/validations/validations";
import { useCompaniesService } from "../../../../services/companiesService";
import { useGeneralService } from "../../../../services/generalService";
import { useUserContext } from "../../../../contexts/userContext";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { AIRLINES, PAGE_TITLES } from "../../../../constants/appConstants";
import { HOME_ROUTE } from "../../../../constants/routes.js";
import { useLocaleContext } from "../../../../contexts/localeContext";
import { useNotificationContext } from "../../../../contexts/notificationContext";
import eventEmitter from '../../../../services/eventEmitterService';
import { useHomepageService } from '../../../../services/homepageService';
import { moveByPriority } from "../../../../helpers/general";
import { taxFieldValidation, taxIdHelperMessageMap, showMilesThresholdChangesAA } from "../../../../helpers/general";
import { maxPromoCodesAllowed } from "../../../../constants/appConstants";

/* Components */
import UCButton from "../../../buttons";
import DeleteCompanyMasterModal from "../../../modals/deleteCompanyMasterModal";
import UCSelect from "../../../ucselect";
import UCCheckbox from "../../../uccheckbox";
import GeneralTabSkeleton from "../../skeletonGeneralTab";
import { ReactComponent as GreenCheckmarkIcon } from "../../../../resources/images/icon-notification-ok.svg";
import { ReactComponent as ErrorIcon } from "../../../../resources/images/red-x-icon.svg";
import { ReactComponent as KeyIcon } from "../../../../resources/images/green-key-icon.svg";
import { ReactComponent as EditEmailSvg } from '../../../../resources/images/edit.svg';
import { ReactComponent as DeleteCompanyIcon } from "../../../../resources/images/delete-company-icon.svg";
import { ReactComponent as WarningCompanyDeleteIcon } from "../../../../resources/images/warning-company-delete-icon.svg";
import { ReactComponent as KeyModalIcon } from "../../../../resources/images/key-icon.svg";
import ConfirmActionModal from "../../../modals/confirmActionModal/index.js";
import UpdateCitiEmailModal from "../../../admin/companyDetailsTabbedNav/updateCitiEmailModal";
import UpdateCitiPcanModal from "../../../modals/updateCitiPcanModal";
import { ReactComponent as NoDiscountsIcon } from "../../../../resources/images/slash-circle.svg";
import { ReactComponent as DiscountsIcon } from "../../../../resources/images/trophy-01.svg";
import UCPhoneCountrySelector from "../../../ucPhoneCountrySelector";
import OverrideDiscountTierModal from "../../../modals/overrideDiscountTierModal";
import MultiValueField from "components/ucMultiValueField";
import InvalidPromoCodesModal from "../../../modals/invalidPromoCodesModal";

/* Styles */
import styles, { countrySelect, selectError } from "./styles.js"
import { colors } from "../../../../constants/colors";
import generalStyles from "../../../../resources/styles/general";



const messages = defineMessages({
  statusPlaceholderMessage: {
    id: 'settings.statusPlaceholder'
  },
  namePlaceholderMessage: {
    id: 'settings.namePlaceholder'
  },
  countryPlaceholderMessage: {
    id: 'settings.countryPlaceholder'
  },
  address1PlaceholderMessage: {
    id: 'settings.address1Placeholder'
  },
  address2PlaceholderMessage: {
    id: 'settings.address2Placeholder'
  },
  cityPlaceholderMessage: {
    id: 'settings.cityPlaceholder'
  },
  statePlaceholderMessage: {
    id: 'settings.statePlaceholder'
  },
  postalCodePlaceholderMessage: {
    id: 'settings.postalCodePlaceholder'
  },
  removePlaceholder: {
    id: 'settings.removePlaceholder'
  },
  accountIdPlaceholderMessage: {
    id: 'settings.accountIdPlaceholder'
  },
  discountCodePlaceholderMessage: {
    id: 'settings.discountCodePlaceholder'
  },
  companySizePlaceholderMessage: {
    id: 'settings.companySizePlaceholder'
  },
  businessWebsitePlaceholderMessage: {
    id: 'settings.businessWebsitePlaceholder'
  },
  referralCodePlaceholderMessage: {
    id: 'settings.referralCodePlaceholder'
  },
  taxIdPlaceholderMessage: {
    id: 'settings.taxIdPlaceholder'
  },
  businessAccNrPlaceholderMessage: {
    id: 'settings.businessAccNrPlaceholder'
  },
  businessNrPlaceholderMessage: {
    id: 'settings.businessNrPlaceholder'
  },
  mileagePlanNumberPlaceholderMessage: {
    id: 'admin.editCompany.mileagePlanNumberPlaceholder'
  },
  arcIataPlaceholderMessage: {
    id: 'admin.editCompany.travelAgencyPlaceholder'
  },
  entityPCANPlaceholderMessage: {
    id: 'settings.entityPCAN'
  },
  primaryTravelManagerAcc: {
    id: 'settings.primaryTravelManagerAcc'
  },
  countryAriaLabel: {
    id: 'settings.countryAriaLabel'
  },
  stateAriaLabel: {
    id: 'settings.stateAriaLabel'
  },
  companySizeAriaLabel: {
    id: 'settings.companySizeAriaLabel'
  },
  companySizeXSmall: {
    id: 'companySizeXSmall'
  },
  companySizeSmall: {
    id: 'companySizeSmall'
  },
  companySizeMedium: {
    id: 'companySizeMedium'
  },
  companySizeLarge: {
    id: 'companySizeLarge'
  },
  companySizeXLarge: {
    id: 'companySizeXLarge'
  },
  deleteBusinessAccAriaLabel: {
    id: 'settings.deleteCompanyButton'
  },
  cancelAriaLabel: {
    id: 'settings.cancel'
  },
  saveAriaLabel: {
    id: 'settings.save'
  },
  promoCodes: {
    id: 'settings.promoCodes'
  }
})


const GeneralTabAA = (props) => {

  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();
  const globalLocale = useLocaleContext();
  const { updateCompany, deleteCompany, getCompanyAdmins, getCompanyDetails, setOverrideThreshold, adminOverrideDiscount } = useCompaniesService();
  const { getStates, getAARegistrationCountries } = useGeneralService();
  const { staticContent, programDetails } = useHomepageService();

  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState(null);

  const { companyInfoMockedApiValidationObj } = useValidationService()
  const [testSchema, setTestSchema] = useState({});

  const [showOverride, setShowOverrideModal] = useState(false);
  const [showRevert, setShowRevertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [states, setStates] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState([])
  const [showUpdateCitiEmailModal, setShowUpdateCitiEmailModal] = useState(false);
  const [showEditPcanModal, setShowEditPcaModal] = useState(false);
  const [fullName, setFullName] = useState("")
  const [countriesData, setCountriesData] = useState([]);
  const companySize = [
    { value: '0', label: intl.formatMessage(messages.companySizeXSmall) },
    { value: '1', label: intl.formatMessage(messages.companySizeSmall) },
    { value: '2', label: intl.formatMessage(messages.companySizeMedium) },
    { value: '3', label: intl.formatMessage(messages.companySizeLarge) },
    { value: '4', label: intl.formatMessage(messages.companySizeXLarge) },
  ];
  const [entityPCANField, setEntityField] = useState(companyDetails?.entityPCAN)
  const [showOverrideDiscountModal, setShowOverrideDiscountModal] = useState(false);
  const [showPromoCodesModal, setShowPromoCodesModal] = useState(false);
  const [invalidPromoCodes, setInvalidPromoCodes] = useState("");

  const userDetails = useUserContext();
  const { initAnalytics } = useAnalytics();
  const { checkPermission } = useHasPermission();
  const { setNotification } = useNotificationContext();

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;


  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["settings"]
    };
    initAnalytics(initUtagData, 'init call', 'view')

    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(companyInfoMockedApiValidationObj);
    setTestSchema(result)
  }, [])

  useEffect(() => {
    (async () => {
      const response = await getCompanyDetails(companyEntityId);
      const countriesResponse = await getAARegistrationCountries();
      setCountriesData(moveByPriority(countriesResponse, "value") || []);
      if (response?.address?.country) {
        handleCountryChange(response.address.country);
      } else {
        handleCountryChange("US")
      }
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
      const responseAdmins = await getCompanyAdmins(companyEntityId);
      setCompanyAdmins(responseAdmins?.usersByRoleDTOList)
    })();
  }, [])

  useEffect(() => {
    const name = companyAdmins?.find(item => item.userEntityId === companyDetails?.primaryAdminId)
    const fullName = name?.firstName + " " + name?.lastName
    setFullName(fullName)
  }, [companyAdmins]);

  useEffect(() => {
    const handleCompanyDeletionPending = async () => {
      const response = await getCompanyDetails(companyEntityId);
      setCompanyDetails({
        ...response,
        companyId: companyEntityId,
      })
    };

    eventEmitter.on('companyDeletionPending', handleCompanyDeletionPending);

    return () => {
      eventEmitter.off('companyDeletionPending', handleCompanyDeletionPending);
    };
  }, [companyEntityId, getCompanyDetails]);

  useEffect(() => {
    setEntityField(companyDetails?.entityPCAN)
  }, [companyDetails]);

  const handleTaxIdBlur = (values, setFieldValue, validateField, setFieldTouched) => {
    if (values.countryCode !== "CL") {
      setFieldValue("taxId", values.taxId.replaceAll("-", ""), false);
    }
    validateField("taxId")
    setFieldTouched("taxId")
  }

  const submitForm = async (values, setSubmitting) => {
    const payload = {
      companyId: companyDetails.companyId,
      primaryAdminId: values.primaryContact,
      companyName: values.companyName,
      taxId: values.taxId,
      phoneCountryCode: values.phoneCountryCode,
      phoneNumber: values.phoneNumber,
      address: {
        country: values.countryCode,
        streetAddress: values.streetAddress1,
        streetAddress2: values.streetAddress2,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
      },
      previousAccountNumber: values.previousAccountNumber,
      discountCode: values.discountCode,
      companySize: values.companySize,
      businessWebsite: values.businessWebsite,
      cargo: values.cargo,
      promoCodes: values.promoCodes
    };

    if (checkPermission("F_VIEW_COMPANY_TAXID")) {
      payload.taxId = values.taxId;
    }

    if (checkPermission("F_COMPANYSETTINGS_REFERRALCODE_VIEW")) {
      payload.referralCode = values.referralCode;
    }

    const response = await updateCompany(payload);
    setSubmitting(false);

    if (response) {
      const invalidCodesError = response?.errors?.find(
        (e) => e.errorCode === "AA201" && e.errorDescription
      );
      if (invalidCodesError) {
        setInvalidPromoCodes(invalidCodesError.errorDescription);
        setShowPromoCodesModal(true);
      }
    }
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const goBack = () => {
    navigate(-1);
  }

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm()
    }
    navigate(HOME_ROUTE);
    window.scrollTo(0, 0);
  }

  const handleCountryChange = async (item) => {
    const response = await getStates(item);
    if (response) {
      setStates(response.states)
    }
  }

  const thMet = companyDetails?.thresholdMet;
  const overriden = companyDetails?.thresholdOverriden;

  const getStatusTitle = () => {
    if (thMet) {
      return <FormattedMessage id="override.threshold.met" />
    } else {
      return <FormattedMessage id="override.threshold.not.met" />
    }
  }

  const milisecondsTimestamp = companyDetails?.thresholdOverridenTimestamp * 1000;

  const getStatusDescription = () => {
    if (thMet || !overriden) {
      //to do change last check date hwne available !
      // return <FormattedMessage id="override.threshold.lastCheck" values={{date: "4/14/24, 12:15 PM"}}/>
      return null
    } else {
      if (companyDetails?.thresholdOverridenBy === "Discount tier")
        return <FormattedMessage id="override.threshold.overridden.discount" values={{ date: moment(milisecondsTimestamp).locale(globalLocale).format('M/D/YY, h:mm A') }} />
      else
        return <FormattedMessage id="override.threshold.overridden" values={{ date: moment(milisecondsTimestamp).locale(globalLocale).format('M/D/YY, h:mm A'), name: companyDetails?.thresholdOverridenBy }} />
    }
  }

  const getStatusIcon = () => {
    if (thMet) {
      return <GreenCheckmarkIcon />
    } else if (!thMet && !overriden) {
      return <ErrorIcon />
    } else if (overriden) {
      return <KeyIcon />
    }
  }

  const fetchCompanyDetails = async () => {
    const response = await getCompanyDetails(companyEntityId);
    setCompanyDetails({
      ...response,
      companyId: companyEntityId,
    });
  };

  const confirmOverride = async () => {
    await setOverrideThreshold(companyEntityId, true)
    setShowOverrideModal(false)
    fetchCompanyDetails()
  }

  const confirmRevert = async () => {
    await setOverrideThreshold(companyEntityId, false)
    setShowRevertModal(false)
    fetchCompanyDetails()
  }

  const maskedTaxId = (taxNumber) => {
    if (!taxNumber || isNaN(taxNumber)) {
      return taxNumber
    } else {
      const numberStr = taxNumber?.toString();
      const lastFourDigits = numberStr?.slice(-4);
      const maskedPart = '*'.repeat(numberStr?.length - 4);
      const maskedNumber = `${maskedPart}${lastFourDigits}`;

      return (maskedNumber)
    }
  };

  const confirmDiscountOverride = async (payload) => {
    await adminOverrideDiscount(payload, companyEntityId)
    setShowOverrideDiscountModal(false)
    fetchCompanyDetails()
  }

  const getDiscountStatusTitle = (value) => {
    switch (value) {
      case 1:
        return <FormattedMessage id="admin.companyDetails.discountTitleTier" values={{ tierName: staticContent?.discountsData?.discounts?.[0]?.title }} />;
        break;
      case 2:
        return <FormattedMessage id="admin.companyDetails.discountTitleTier" values={{ tierName: staticContent?.discountsData?.discounts?.[1]?.title }} />;
        break;
      case 3:
        return <FormattedMessage id="admin.companyDetails.discountTitleTier" values={{ tierName: staticContent?.discountsData?.discounts?.[2]?.title }} />;
        break;
      default:
        return <FormattedMessage id="admin.companyDetails.noDiscountTitle" values={{ tierName: staticContent?.discountsData?.noTierTitle }} />;
    }
  }

  const getDiscountStatusErrorDescription = (history) => {
    let messages = [];
    history.forEach(entry => {
      switch (entry.status) {
        case "DECLINED":
          messages.push(<FormattedMessage
            id="admin.companyDetails.termsDenied"
            values={{ date: moment(entry.dateTime).format("M/D/YY, h:mm A"), name: entry.userName }} />);
          break;
        case "REMOVED":
          messages.push(<FormattedMessage
            id="admin.companyDetails.tierRemoved"
            values={{ date: moment(entry.dateTime).format("M/D/YY, h:mm A"), name: entry.userName }} />);
          break;
        default:
          break;
      }
    })
    return messages
  }

  const getDiscountStatusDescription = (history) => {
    let messages = [];
    history.forEach(entry => {
      switch (entry.status) {
        case "QUALIFIED":
          messages.push(<FormattedMessage id="admin.companyDetails.discountAchived" values={{ date: moment(entry.dateTime).format("M/D/YY, h:mm A") }} />);
          break;
        case "OVERRIDDEN":
          messages.push(<FormattedMessage id="admin.companyDetails.overridden" values={{ date: moment(entry.dateTime).format("M/D/YY, h:mm A"), name: entry.userName }} />);
          break;
        default:
          break;
      }
    });
    return messages
  }

  return (
    <>
      <div className={classes.details}>
        <div>
          <h2 className={classes.title}>
            <FormattedMessage id="settings.titleSection" />
          </h2>
          <div className={classes.description}>
            <FormattedMessage id="settings.description" />
          </div>
        </div>
        {checkPermission("F_COMPANYSETTINGS_EDITSETTINGS") && (
          <div className={classes.btnContainer}>
            {companyDetails && companyAdmins?.length > 0 && states.length ?
              <>
                <UCButton
                  type="default"
                  className={classes.buttonCancel}
                  onClick={cancelUpdate}
                  text={<FormattedMessage id="settings.cancel" />}
                  tabIndex={0}
                  ariaLabel={intl.formatMessage(messages.cancelAriaLabel)}
                />
                <UCButton
                  type="primary"
                  className={classes.button}
                  onClick={handleFormSubmit}
                  text={<FormattedMessage id="settings.save" />}
                  spinner={{
                    name: "updateCompanySpinner",
                    size: 20,
                    color: colors.white,
                  }}
                  tabIndex={0}
                  ariaLabel={intl.formatMessage(messages.saveAriaLabel)}
                />
              </>
              :
              <div className={classes.secondRowAuto}>
                <Skeleton count={1} width={68} className={classes.longSkM} />
                <Skeleton count={1} width={68} className={classes.longSk} />
              </div>
            }
          </div>
        )}
      </div>

      <div className={classes.dividerContainer}>
        <hr className={classes.border} />
      </div>
      {companyDetails && companyAdmins?.length > 0 && states.length > 0 ? (
        <div className={classes.editCompany}>
          {
            companyDetails &&
            <Formik
              innerRef={formRef}
              enableReinitialize={true}
              initialValues={{
                primaryContact: companyDetails?.primaryAdminId,
                companyName: companyDetails?.companyName,
                countryCode: companyDetails?.address?.country,
                streetAddress1: companyDetails?.address?.streetAddress,
                streetAddress2: companyDetails?.address?.streetAddress2 ? companyDetails?.address?.streetAddress2 : "",
                city: companyDetails?.address?.city,
                state: companyDetails?.address?.state,
                postalCode: companyDetails?.address?.postalCode,
                businessWebsite: companyDetails?.businessWebsite ? companyDetails?.businessWebsite : "",
                phoneCountryCode: companyDetails?.phoneCountryCode ? companyDetails?.phoneCountryCode : "US",
                phoneNumber: companyDetails?.phoneNumber?.trim() || "",
                taxId: companyDetails?.taxId ? companyDetails?.taxId : "",
                discountCode: companyDetails?.discountCode,
                companySize: companyDetails?.companySize,
                referralCode: companyDetails?.referralCode ? companyDetails?.referralCode : "",
                previousAccountNumber: companyDetails?.previousAccountNumber ? companyDetails?.previousAccountNumber : "",
                businessAccountNumber: companyDetails?.companyMembershipNumber,
                entityPCAN: entityPCANField ? entityPCANField : "",
                cargo: companyDetails?.cargo ? companyDetails?.cargo : false,
                fullName: fullName ? fullName : "",
                promoCodes: companyDetails?.promoCodes ? companyDetails?.promoCodes : []
              }}
              validationSchema={testSchema}
              onSubmit={(values, { setSubmitting }) => {
                submitForm(values, setSubmitting);
              }}
            >
              {({
                values,
                errors,
                touched,
                setFieldTouched,
                handleChange,
                handleBlur,
                handleSubmit,
                resetForm,
                isSubmitting,
                setFieldValue,
                validateField,
                enableReinitialize
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-xl-4">
                        <h2 className={classes.location}>
                          <FormattedMessage id="settings.contact" />
                        </h2>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.contactDescription" />
                        </div>
                      </div>
                      {companyAdmins?.length > 0 && !companyDetails?.mustChangeEmailAddress && (
                        <div className="col-12 col-xl-8">
                          <div className={classes.formContainer}>
                            <Field
                              placeholder={"primaryContact"}
                              type="select"
                              name="primaryContact"
                              id="primaryContact"
                              aria-describedby="primaryContactSubtext"
                              render={({ form }) => (
                                <div>
                                  <label htmlFor="primaryContact" className={classes.label}>
                                    <FormattedMessage id="settings.primaryContact" />
                                  </label>
                                  <UCSelect
                                    className={classes.roles}
                                    displayName={<FormattedMessage id="settings.primaryContact" />}
                                    options={companyAdmins}
                                    defaultValue={companyAdmins?.find(item => item.userEntityId === values.primaryContact)}
                                    onChange={(item) => form.setFieldValue("primaryContact", item.userEntityId)}
                                    formatOptionLabel={(option) => (
                                      <div className={classes.adminLabel}>
                                        <div className={classes.adminName}>{option.firstName} {option.lastName}</div>
                                      </div>
                                    )}
                                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                    ariaLabel={intl.formatMessage(messages.primaryTravelManagerAcc)}
                                    getOptionValue={(option) => option.userEntityId}
                                    disabled={!companyDetails?.canUpdatePrimaryAdmin}
                                  />
                                  <ErrorMessage
                                    name="primaryContact"
                                    component="span"
                                    className={classes.error}
                                  />
                                  <p id="primaryContactSubtext" className={classes.pContactSubtext}>
                                    <FormattedMessage id="settings.primaryContact.subText" />
                                  </p>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {companyDetails?.mustChangeEmailAddress
                        && (
                          <div className="col-12 col-xl-8">
                            <div className={classes.formContainer}>
                              <div className={classes.inputMailContainer}>
                                <label htmlFor="status" className={classes.label}>
                                  <FormattedMessage id="admin.editCompany.primaryContact" />
                                </label>
                              </div>
                              <div className={classes.editDiv}>
                                <Field
                                  placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                                  type="text"
                                  name="travelManager"
                                  id="travelManager"
                                  value={fullName}
                                  className={
                                    errors.travelManager && touched.travelManager
                                      ? "input-error"
                                      : null
                                  }
                                  disabled={true}
                                />
                                <UCButton
                                  onClick={() => setShowUpdateCitiEmailModal(true)}
                                  className={classes.editButton}
                                  iconEnd={<EditEmailSvg />}
                                />
                              </div>
                              <ErrorMessage
                                name="travelManeger"
                                component="span"
                                className={classes.error}
                              />
                              <span className={classes.taxIdNote}>
                                <FormattedMessage id={"admin.updateCitiEmailText"} />
                              </span>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  <div className={classes.formCont}>
                    <div className="row">
                      <div className="col-12 col-xl-4">
                        <h2 className={classes.location}>
                          <FormattedMessage id="settings.location" />
                        </h2>
                        <div className={classes.description}>
                          <FormattedMessage id="settings.update" />
                        </div>
                      </div>
                      <div className="col-12 col-xl-8">
                        <div className={classes.formContainer}>
                          <div className={classes.inputContainer}>
                            <div>
                              <label htmlFor="companyName" className={classes.label}>
                                <FormattedMessage id="settings.companyName" />
                              </label>
                              <span aria-hidden="true">*</span>
                            </div>
                            <Field
                              placeholder={intl.formatMessage(messages.namePlaceholderMessage)}
                              type="text"
                              name="companyName"
                              id="companyName"
                              aria-describedby="companyName-error"
                              className={
                                errors.companyName && touched.companyName
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="companyName"
                              component="span"
                              className={classes.error}
                              id="companyName-error"
                              role="alert"
                              aria-live="assertive"
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <Field
                              type="select"
                              name="countryCode"
                              id="countryCode"
                              render={({ form }) => (
                                <div>
                                  <div>
                                    <label htmlFor="countryCode" className={classes.label}>
                                      <FormattedMessage id="settings.country" />
                                    </label>
                                    <span aria-hidden="true">*</span>
                                  </div>
                                  <UCSelect
                                    isSearchable={true}
                                    className={classes.roles}
                                    styles={countrySelect}
                                    displayName={<FormattedMessage id="settings.countryPlaceholder" />}
                                    options={countriesData}
                                    defaultValue={countriesData.find(item => item.value === values.countryCode)}
                                    value={values.countryCode}
                                    getOptionLabel={(option) => option.label}
                                    ariaLabel={intl.formatMessage(messages.countryAriaLabel)}
                                    onBlur={() => setFieldTouched("countryCode", true)}
                                    disabled={!checkPermission("F_COMPANYSETTINGS_COUNTRY_EDIT")}
                                    onChange={(item) => {
                                      form.setFieldValue("countryCode", item.value)
                                      handleCountryChange(item.value)
                                      {
                                        item.value !== values.countryCode ? form.setFieldValue("state", " ") : null
                                      }
                                    }}
                                  />
                                  <ErrorMessage
                                    name="countryCode"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              )}
                              className={
                                errors.countryCode && touched.countryCode
                                  ? "input-error"
                                  : null
                              }
                            />
                          </div>
                          {checkPermission("F_VIEW_COMPANY_TAXID") &&
                            <div className={classes.inputContainer}>
                              <label htmlFor="taxId" className={classes.label}>
                                {values.countryCode === 'CA' ? (
                                  <FormattedMessage id='settings.businessNr' />
                                ) : (
                                  <FormattedMessage id='settings.taxId' />
                                )}
                              </label>
                              <Field
                                placeholder={
                                  values.countryCode === 'CA'
                                    ? intl.formatMessage(messages.businessNrPlaceholderMessage)
                                    : intl.formatMessage(messages.taxIdPlaceholderMessage)
                                }
                                onBlur={() => handleTaxIdBlur(values, setFieldValue, validateField, setFieldTouched)}
                                type="text"
                                name="taxId"
                                id="taxId"
                                value={!checkPermission("F_PROVIDER_ADMIN") && providerCode === AIRLINES.AS && companyDetails?.taxId ? maskedTaxId(values?.taxId) : values.taxId}
                                validate={() => !companyDetails?.taxId ? taxFieldValidation(values) : null}
                                className={
                                  errors.taxId && touched.taxId
                                    ? "input-error"
                                    : null
                                }
                                disabled={checkPermission("F_EDIT_COMPANY_TAXID") === false && !!companyDetails?.taxId && !checkPermission("F_PROVIDER_ADMIN")}
                              />
                              {
                                !companyDetails?.taxId && providerCode === AIRLINES.AA &&
                                <span className={classes.taxIdNote}>
                                  {values.countryCode && <FormattedMessage id={`${taxIdHelperMessageMap[values.countryCode]}`} />}
                                </span>
                              }
                              <ErrorMessage
                                name="taxId"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          }
                          <fieldset className={classes.phoneInputContainer}>
                            <div>
                              <legend htmlFor="phoneNumber" className={classes.label}>
                                <FormattedMessage id="settings.phoneNumber" />
                              </legend>
                              <span aria-hidden="true">*</span>
                            </div>
                            <UCPhoneCountrySelector
                              providerCode={providerCode}
                              errors={errors}
                              touched={touched}
                              values={values}
                              aria-describedby="phoneNumber-error"
                            />
                            <ErrorMessage
                              name="phoneNumber"
                              component="span"
                              className={classes.error}
                              id="phoneNumber-error"
                              role="alert"
                              aria-live="assertive"
                            />
                          </fieldset>
                          <div className={classes.inputContainer}>
                            <div>
                              <label htmlFor="streetAddress1" className={classes.label}>
                                <FormattedMessage id="settings.address1" />
                              </label>
                              <span aria-hidden="true">*</span>
                            </div>
                            <Field
                              placeholder={intl.formatMessage(messages.address1PlaceholderMessage)}
                              type="text"
                              name="streetAddress1"
                              id="streetAddress1"
                              aria-describedby="streetAddress1-error"
                              className={
                                errors.streetAddress1
                                  && touched.streetAddress1
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="streetAddress1"
                              component="span"
                              className={classes.error}
                              id="streetAddress1-error"
                              role="alert"
                              aria-live="assertive"
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <label htmlFor="streetAddress2" className={classes.label}>
                              <FormattedMessage id="settings.address2" />
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.address2PlaceholderMessage)}
                              type="text"
                              name="streetAddress2"
                              id="streetAddress2"
                              className={
                                errors.streetAddress2 && touched.streetAddress2
                                  ? 'input-error'
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="streetAddress2"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                          <div className={classes.inputContainer}>
                            <div>
                              <label htmlFor="city" className={classes.label}>
                                <FormattedMessage id="settings.city" />
                              </label>
                              <span aria-hidden="true">*</span>
                            </div>
                            <Field
                              placeholder={intl.formatMessage(messages.cityPlaceholderMessage)}
                              type="text"
                              name="city"
                              id="city"
                              aria-describedby="city-error"
                              className={
                                errors.city && touched.city
                                  ? 'input-error'
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="city"
                              component="span"
                              className={classes.error}
                              id="city-error"
                              role="alert"
                              aria-live="assertive"
                            />
                          </div>
                          {
                            states &&
                            <div className={classes.inputContainer}>
                              <label htmlFor="state" className={classes.label}>
                                <FormattedMessage id="settings.state" />
                              </label>
                              <Field
                                type="select"
                                name="state"
                                id="state"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      className={classes.roles}
                                      styles={errors.state && touched.state && selectError}
                                      displayName={<FormattedMessage id="settings.statePlaceholder" />}
                                      ariaLabel={intl.formatMessage(messages.stateAriaLabel)}
                                      getOptionLabel={(option) => option.label}
                                      value={values.state}
                                      options={states}
                                      onBlur={() => setFieldTouched("state", true)}
                                      defaultValue={states.find(item => item.value === values.state)}
                                      onChange={(item) => form.setFieldValue("state", item.value)}
                                      isSearchable={true}
                                    />
                                    <ErrorMessage
                                      name="state"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                                className={
                                  errors.state && touched.state
                                    ? "input-error"
                                    : null
                                }
                              />
                            </div>
                          }
                          <div className={`${classes.inputContainer} ${classes.lastInputContainer}`}>
                            <label htmlFor="postalCode" className={classes.label}>
                              <FormattedMessage id="settings.postalCode" />
                              <span aria-hidden="true">*</span>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.postalCodePlaceholderMessage)}
                              type="postalCode"
                              name="postalCode"
                              id="postalCode"
                              aria-describedby="postalCode-error"
                              className={
                                errors.postalCode && touched.postalCode
                                  ? "input-error"
                                  : null
                              }
                            />
                            <ErrorMessage
                              name="postalCode"
                              component="span"
                              className={classes.error}
                              id="postalCode-error"
                              role="alert"
                              aria-live="assertive"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={classes.dividerContainer}>
                      <hr className={classes.border} />
                    </div>

                    <div className={classes.formCont}>
                      <div className="row">
                        <div className="col-12 col-xl-4">
                          <h2 className={classes.location}>
                            <FormattedMessage id="settings.membership" />
                          </h2>
                          <div className={classes.description}>
                            <FormattedMessage id="settings.membershipDescription" />
                          </div>
                        </div>
                        <div className="col-12 col-xl-8">
                          <div className={classes.formContainer}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="businessAccountNumber" className={classes.label}>
                                <FormattedMessage id="settings.businessAccountNumber" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.businessAccNrPlaceholderMessage)}
                                type="text"
                                name="businessAccountNumber"
                                id="businessAccountNumber"
                                disabled={providerCode !== AIRLINES.AS ? true : (providerCode === AIRLINES.AS && checkPermission("F_PROVIDER_ADMIN")) ? false : true}
                              />
                            </div>

                            <div className={classes.inputContainer}>
                              <label htmlFor="previousAccountNumber" className={classes.label}>
                                <FormattedMessage id="settings.accountId" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.accountIdPlaceholderMessage)}
                                type="text"
                                name="previousAccountNumber"
                                id="previousAccountNumber"
                                aria-describedby="previousAccountNumberSubtext"
                                className={
                                  errors.previousAccountNumber && touched.previousAccountNumber
                                    ? "input-error"
                                    : null
                                }
                              />
                              <ErrorMessage
                                name="previousAccountNumber"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            <div id="previousAccountNumberSubtext" className={classes.taxExampleContainer}>
                              <FormattedMessage id='settings.accountExample' />
                            </div>

                            {checkPermission("F_COMPANY_EDITCITI_INFO") && companyDetails?.companyType == "Citi" &&
                              <>
                                <div className={`${classes.inputMailContainer} ${classes.citiPcanDiv}`}>
                                  <label htmlFor="entityPCAN" className={classes.label}>
                                    <FormattedMessage id="settings.entityPCAN" />
                                    <span><FormattedMessage id="general.optional" /></span>
                                  </label>
                                  <div className={classes.editDiv}>
                                    <Field
                                      placeholder={intl.formatMessage(messages.entityPCANPlaceholderMessage)}
                                      type="text"
                                      name="entityPCAN"
                                      id="entityPCAN"
                                      className={
                                        errors.entityPCAN && touched.entityPCAN
                                          ? "input-error"
                                          : null
                                      }
                                      disabled={true}
                                    />
                                    <UCButton
                                      onClick={() => setShowEditPcaModal(true)}
                                      className={classes.editButton}
                                      iconEnd={<EditEmailSvg />}
                                    />
                                  </div>
                                  <ErrorMessage
                                    name="entityPCAN"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              </>
                            }

                            {
                              companyDetails?.viewDiscountCode &&
                              <div className={classes.inputContainer}>
                                <label htmlFor="discountCode" className={classes.label}>
                                  <FormattedMessage id="settings.discountCode" />
                                </label>
                                <Field
                                  placeholder={intl.formatMessage(messages.discountCodePlaceholderMessage)}
                                  type="text"
                                  name="discountCode"
                                  id="discountCode"
                                  className={
                                    errors.discountCode && touched.discountCode
                                      ? "input-error"
                                      : null
                                  }
                                  disabled={!companyDetails?.editDiscountCode}
                                />
                                <ErrorMessage
                                  name="discountCode"
                                  component="span"
                                  className={classes.error}
                                />
                              </div>
                            }
                            <div className={classes.inputContainer}>
                              <label htmlFor="companySize" className={classes.label}>
                                <FormattedMessage id="settings.companySize" />
                              </label>
                              <Field
                                name="companySize"
                                id="companySize"
                                render={({ form }) => (
                                  <div>
                                    <UCSelect
                                      className={classes.roles}
                                      displayName={<FormattedMessage id="settings.companySize" />}
                                      ariaLabel={intl.formatMessage(messages.companySizeAriaLabel)}
                                      options={companySize}
                                      defaultValue={companySize.find(item => item.value === values.companySize)}
                                      onChange={(item) => form.setFieldValue("companySize", item.value)}
                                    />
                                    <ErrorMessage
                                      name="companySize"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                                className={
                                  errors.companySize && touched.companySize
                                    ? "input-error"
                                    : null
                                }
                              />
                              <ErrorMessage
                                name="companySize"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            <div className={classes.inputContainer}>
                              <label htmlFor="businessWebsite" className={classes.label}>
                                <FormattedMessage id="settings.businessWebsite" />
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.businessWebsitePlaceholderMessage)}
                                type="text"
                                name="businessWebsite"
                                id="businessWebsite"
                                className={
                                  errors.businessWebsite && touched.businessWebsite
                                    ? "input-error"
                                    : null
                                }
                              />
                              <ErrorMessage
                                name="businessWebsite"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                            {checkPermission("F_COMPANYSETTINGS_REFERRALCODE_VIEW") &&
                              <>
                                <div className={`${classes.inputContainer} ${classes.refferalInputContainer}`}>
                                  <label htmlFor="referralCode" className={classes.label}>
                                    <FormattedMessage id="settings.referralCode" />
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.referralCodePlaceholderMessage)}
                                    type="text"
                                    name="referralCode"
                                    id="referralCode"
                                    className={
                                      errors.referralCode && touched.referralCode
                                        ? "input-error"
                                        : null
                                    }
                                  />
                                  <ErrorMessage
                                    name="referralCode"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                                <div className={classes.referralInfoContainer}>
                                  <span className={classes.referralCodeInfoText}><FormattedMessage id='settings.referralCodeInfoText' /></span>
                                </div>
                              </>
                            }
                            {checkPermission("F_COMPANY_PROMOCODES_VIEW") &&
                              <>
                                <div className={`${classes.promoCodesContainer}`}>
                                  <label htmlFor="promoCodes" className={classes.label}>
                                    <FormattedMessage id="settings.promoCodes" />
                                  </label>
                                  <MultiValueField
                                    name="promoCodes"
                                    values={values.promoCodes}
                                    onValuesChange={item => setFieldValue("promoCodes", item)}
                                    setFieldTouched={setFieldTouched}
                                    placeholder={intl.formatMessage(messages.promoCodes)}
                                    maxValues={maxPromoCodesAllowed}
                                    errors={errors}
                                    ariaDescribedby={"promoCodes-error"}
                                  />
                                  <ErrorMessage
                                    name="promoCodes"
                                    component="span"
                                    className={classes.error}
                                    id="promoCodes-error"
                                    role="alert"
                                    aria-live="assertive"
                                  />
                                </div>
                                <div className={classes.promoInfoContainer}>
                                  <span className={classes.promoCodeInfoText}><FormattedMessage id='settings.promoCodeInfoText' /></span>
                                </div>
                              </>
                            }
                            <div className={classes.cargoCheckboxWrapper}>
                              <Field
                                name="cargo"
                                type="checkbox"
                                render={({ field, form }) => (
                                  <UCCheckbox
                                    className={field.value ? classes.cargoCheckboxChecked : classes.cargoCheckbox}
                                    labelClass={classes.labelClass}
                                    checked={field.value}
                                    onChange={() => {
                                      if (checkPermission("F_COMPANY_CHANGE_SUBTYPE")) {
                                        form.setFieldValue('cargo', !field.value);
                                      }
                                    }}
                                    label={<FormattedMessage id="settings.cargoCompany" />}
                                    // Disable checkbox if user does not have permission
                                    disabled={!checkPermission("F_COMPANY_CHANGE_SUBTYPE")}
                                  />
                                )}
                              />
                              <ErrorMessage
                                name="cargo"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.dividerContainer}>
                    <hr className={classes.border} />
                  </div>

                  {!showMilesThresholdChangesAA(programDetails.thresholdLogic) && (
                    <>
                      {userDetails?.manageCompany && (
                        <>
                          <div className="row">
                            <div className="col-12 col-xl-4">
                              <h2 className={classes.location}>
                                <FormattedMessage id="override.threshold.miles.details" />
                              </h2>
                              <div className={classes.description}>
                                <FormattedMessage id="override.threshold.miles.details.desc" />
                              </div>
                            </div>
                            <div className={`col-12 col-xl-8 ${classes.colContentWrapper}`}>
                              <div className={classes.statusDiv}>
                                {getStatusIcon()}
                                <div className={classes.textDiv}>
                                  <div className={classes.thrTitle}>
                                    {getStatusTitle()}
                                  </div>
                                  <div className={classes.thrDescription}>
                                    {getStatusDescription()}
                                  </div>
                                </div>
                              </div>
                              {!thMet && (
                                <UCButton
                                  type="link"
                                  className={classes.thresholdStatusBtn}
                                  text={overriden ? <FormattedMessage id="override.threshold.revert" /> : <FormattedMessage id="override.threshold" />}
                                  onClick={() => overriden ? setShowRevertModal(true) : setShowOverrideModal(true)}
                                />
                              )}
                            </div>
                          </div>

                          <div className={classes.dividerContainer}>
                            <hr className={classes.border} />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {userDetails?.manageCompany && checkPermission("F_DISCOUNTTIER_OVERRIDETIERLEVEL") && (
                    <>
                      <div className={`row ${classes.containerGap}`}>
                        <div className="col-12 col-xl-4">
                          <h2 className={classes.location}>
                            <FormattedMessage id="admin.companyDetails.discountTitle" />
                          </h2>
                          <div className={classes.description}>
                            <FormattedMessage id="admin.companyDetails.discountDescription" />
                          </div>
                        </div>
                        <div className={`col-12 col-xl-8 ${classes.colContentWrapper}`}>
                          <div className={classes.statusDiv}>
                            {companyDetails?.discount?.activeTier ?
                              (companyDetails?.discount?.activeTier?.tierCode === "" ? <NoDiscountsIcon /> : <DiscountsIcon />)
                              : <NoDiscountsIcon />}
                            <div className={classes.textDiv}>
                              <div className={classes.thrTitle}>
                                {getDiscountStatusTitle(companyDetails?.discount?.activeTier?.tierId)}
                              </div>
                              <div className={classes.thrDescription}>
                                {companyDetails?.discount?.activeTier &&
                                  <>
                                    {getDiscountStatusDescription(companyDetails?.discount?.activeTier?.tierHistory).map((message, index) => (
                                      <div key={index}>{message}</div>))}
                                    {getDiscountStatusErrorDescription(companyDetails?.discount?.activeTier?.tierHistory).map((message, index) => (
                                      <div className={classes.discErrorDescription} key={index}>{message}</div>))}
                                  </>}
                              </div>
                            </div>
                          </div>
                          <UCButton
                            type="link"
                            className={classes.thresholdStatusBtn}
                            text={companyDetails?.discount?.activeTier ?
                              <FormattedMessage id="admin.companyDetails.discountChange" /> :
                              <FormattedMessage id="admin.companyDetails.discountOverride" />}
                            onClick={() => setShowOverrideDiscountModal(true)}
                          />
                        </div>
                      </div>
                      <div className={classes.dividerContainer}>
                        <hr className={classes.border} />
                      </div>
                    </>)
                  }


                  {checkPermission("F_COMPANY_DELETE") && companyDetails?.companyStatus && (
                    <div>
                      <div className={classes.formCont}>
                        <div className={`row ${classes.descriptionGap}`}>
                          <div className="col-12 col-xl-4">
                            <h2 className={classes.location}>
                              <FormattedMessage id="settings.deleteBusiness" />
                            </h2>
                            <div className={classes.description}>
                              <FormattedMessage id="settings.deleteBusinessDescription" />
                            </div>
                          </div>

                          {companyDetails?.companyStatus === "ACTIVE" &&
                            <div className={`col-12 col-xl-8 ${classes.deleteButtonContainer}`}>
                              <UCButton
                                type="danger"
                                className={classes.deleteCompanyButton}
                                text={<FormattedMessage id="settings.deleteCompanyButton" />}
                                onClick={() => setShowDeleteModal(true)}
                                tabIndex={0}
                                ariaLabel={intl.formatMessage(messages.deleteBusinessAccAriaLabel)}
                              />
                            </div>
                          }

                          {companyDetails?.companyStatus === "PENDING_REMOVAL" &&
                            <div className="col-12 col-xl-8">
                              <div className="row">
                                <div className={`col-12 col-md-auto ${classes.warningIconContainer}`}><WarningCompanyDeleteIcon /></div>
                                <div className="col-12 col-md">
                                  <p className={classes.accountClosedTitle}><FormattedMessage id="settings.accountClosedTitle" /></p>
                                  <p className={classes.accountClosedDescription}><FormattedMessage id="settings.accountClosedPendingDescription" values={{
                                    atmName: <span className={classes.accountClosedCustomValues}>{companyDetails?.removalRequestedByFirstName} {companyDetails?.removalRequestedByLastName}</span>,
                                    removalDate: <span className={classes.accountClosedCustomValues}>{companyDetails?.removalDate}</span>
                                  }} /></p>
                                </div>
                              </div>
                            </div>
                          }

                        </div>
                      </div>
                      <div className={classes.dividerContainer}>
                        <hr className={classes.border} />
                      </div>
                    </div>
                  )}
                  {checkPermission("F_COMPANYSETTINGS_EDITSETTINGS") && (
                    <Row>
                      <Col>
                        <div className={classes.buttonContainer}>
                          <div className={classes.btnContainer}>
                            <UCButton
                              type="default"
                              className={classes.buttonCancel}
                              onClick={cancelUpdate}
                              text={<FormattedMessage id="settings.cancel" />}
                              tabIndex={0}
                              ariaLabel={intl.formatMessage(messages.cancelAriaLabel)}
                            />
                            <UCButton
                              type="primary"
                              className={classes.button}
                              onClick={handleFormSubmit}
                              text={<FormattedMessage id="settings.save" />}
                              spinner={{
                                name: "updateCompanySpinner",
                                size: 20,
                                color: colors.white
                              }}
                              tabIndex={0}
                              ariaLabel={intl.formatMessage(messages.saveAriaLabel)}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                </form>
              )}
            </Formik>
          }

          {
            showDeleteModal &&
              <DeleteCompanyMasterModal
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                modalIcon={<DeleteCompanyIcon />}
                setSelectedRow={(value) => console.log(value)}
              />
          }

          <ConfirmActionModal
            showModal={showOverride}
            setShowModal={setShowOverrideModal}
            modalIcon={<KeyModalIcon />}
            modalTitle={<FormattedMessage id="override.threshold.modal.title" />}
            modalDescription={<FormattedMessage id="override.threshold.modal.description" />}
            submitCallback={confirmOverride}
            btnSpinner="overrideThresholdSpinner"
          />

          <ConfirmActionModal
            showModal={showRevert}
            setShowModal={setShowRevertModal}
            modalIcon={<KeyModalIcon />}
            modalTitle={<FormattedMessage id="override.threshold.revert.modal.title" />}
            modalDescription={<FormattedMessage id="override.threshold.revert.modal.description" />}
            submitCallback={confirmRevert}
            btnSpinner="revertThresholdSpinner"
          />
          {companyDetails?.mustChangeEmailAddress &&
            <UpdateCitiEmailModal
              showUpdateCitiEmailModal={showUpdateCitiEmailModal}
              setShowUpdateCitiEmailModal={setShowUpdateCitiEmailModal}
              companyDetails={companyDetails}
            />
          }
          <UpdateCitiPcanModal
            showEditPcanModal={showEditPcanModal}
            setShowEditPcaModal={setShowEditPcaModal}
            companyEntityId={companyEntityId}
            setEntityField={setEntityField}
            entityPCAN={entityPCANField}
            entityPCANLimit={companyDetails?.entityPCANLimit}
          />
          <OverrideDiscountTierModal
            showModal={showOverrideDiscountModal}
            setShowModal={setShowOverrideDiscountModal}
            discountData={companyDetails?.discount}
            submitCallback={confirmDiscountOverride}
            staticContent={staticContent}
          />
          <InvalidPromoCodesModal
            showPromoCodesModal={showPromoCodesModal}
            setShowPromoCodesModal={setShowPromoCodesModal}
            codes={invalidPromoCodes}
            onExited={async () => {
              setNotification(
                "success",
                <FormattedMessage id="notification.updateCompanySuccessTitle" />,
                <FormattedMessage id="notification.updateCompanySuccessText" />,
                true,
              );
              await fetchCompanyDetails();
              formRef.current?.resetForm();
            }}
          />
        </div>
      ) : (
        <GeneralTabSkeleton />
      )}
    </>
  )
}

export default GeneralTabAA;
